/**
 * Long / Short Position (risk-reward) planning tool — pure geometry and math.
 *
 * briefs/LONG_SHORT_POSITION_TOOL_BRIEF.md. THIS IS A PLANNING OVERLAY, NOT A
 * POSITION: nothing here is broker data, nothing here talks to an order route,
 * and the drawing type is deliberately `rr_position` (not `position`) so it can
 * never be confused with the broker-position modules (positionExit,
 * positionClosure, ...) that share this bundle.
 *
 * Schema — the whole tool lives in the existing p1/p2/p3 slots, which is what
 * lets handles, the drag snapshot and undo work with no change:
 *   p1 = { time: left edge,  price: ENTRY  }
 *   p2 = { time: right edge, price: STOP   }
 *   p3 = { time: right edge, price: TARGET }
 * Invariants (enforced by normalizePosition, not by the page):
 *   long : stop < entry < target      short: target < entry < stop
 *   p2.time === p3.time  >  p1.time
 *   |entry - stop| and |entry - target| >= MIN_PRICE_GAP
 *
 * This module loads on EVERY page (nc-core.js is loaded by live.html too), so it
 * holds no module-level mutable state and does nothing at import time.
 */

export type PositionSide = "long" | "short";

export interface PositionPoint {
  time: number;
  price: number;
}

export interface PositionLike {
  type?: string;
  side?: string;
  p1?: PositionPoint;
  p2?: PositionPoint;
  p3?: PositionPoint;
  [key: string]: any;
}

/** Drawing `type` persisted for this tool. */
export const POSITION_TYPE = "rr_position";

/** Default reward:risk multiple used to place the target on creation. */
export const DEFAULT_RR = 2;

/**
 * Smallest allowed distance between entry and stop, and entry and target.
 * ASSUMPTION (brief §5.4): 0.05 is the tick of the chartable instruments. It is
 * NOT confirmed per symbol — establish from the resolved instrument row.
 */
export const MIN_PRICE_GAP = 0.05;

/** Default colours. Frozen: this module is bundled onto the live page. */
export const POSITION_COLORS = Object.freeze({
  entry: "#787B86",
  profit: "#2DBE8E",
  loss: "#F06A6A",
});

export interface PositionOptions {
  /** Minimum price gap, default MIN_PRICE_GAP. */
  minPriceGap?: number;
  /** Minimum box width in time units (seconds), default 1. The page passes one candle. */
  minTimeGap?: number;
}

export interface PositionMetrics {
  /** Distance entry -> stop, always positive (points). */
  risk: number;
  /** Distance entry -> target, always positive (points). */
  reward: number;
  /** reward / risk, from the stored values (never from rounded display values). */
  rr: number;
  /** risk as a percentage of entry. */
  riskPct: number;
  /** reward as a percentage of entry. */
  rewardPct: number;
}

/**
 * Float tolerance for the gap comparisons. Without it an exact one-tick gap can be
 * rejected: 100 - 99.95 is 0.04999999999999716 in IEEE-754, which is < 0.05.
 */
const GAP_EPSILON = 1e-9;

function isFiniteNumber(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

function isPoint(p: PositionPoint | undefined | null): p is PositionPoint {
  return !!p && isFiniteNumber(p.time) && isFiniteNumber(p.price);
}

export function isPositionSide(side: unknown): side is PositionSide {
  return side === "long" || side === "short";
}

/**
 * True when `stop` is on the correct side of `entry` by at least the minimum gap:
 * long -> below, short -> above.
 */
export function isValidStop(
  side: PositionSide,
  entry: number,
  stop: number,
  minGap: number = MIN_PRICE_GAP,
): boolean {
  if (!isFiniteNumber(entry) || !isFiniteNumber(stop)) return false;
  return side === "long"
    ? entry - stop >= minGap - GAP_EPSILON
    : stop - entry >= minGap - GAP_EPSILON;
}

/**
 * True when a second click is a valid stop: right side of entry AND a later time.
 * An invalid click is ignored by the page, and the preview is not drawn.
 */
export function isValidSecondClick(
  side: PositionSide,
  entry: PositionPoint | null | undefined,
  stopPoint: PositionPoint | null | undefined,
  minGap: number = MIN_PRICE_GAP,
): boolean {
  if (!isPoint(entry) || !isPoint(stopPoint)) return false;
  if (stopPoint.time <= entry.time) return false;
  return isValidStop(side, entry.price, stopPoint.price, minGap);
}

/**
 * Target price at `rr` times the risk: long -> entry + rr*(entry-stop),
 * short -> entry - rr*(stop-entry). Returns null for an invalid stop or rr.
 */
export function defaultTarget(
  side: PositionSide,
  entry: number,
  stop: number,
  rr: number = DEFAULT_RR,
  minGap: number = MIN_PRICE_GAP,
): number | null {
  if (!isFiniteNumber(rr) || rr <= 0) return null;
  if (!isValidStop(side, entry, stop, minGap)) return null;
  return side === "long" ? entry + rr * (entry - stop) : entry - rr * (stop - entry);
}

/**
 * Risk / reward / R:R / percentages, or null when the drawing is not a valid
 * position (missing points, stop or target on the wrong side, non-positive
 * entry). The renderer draws nothing for null — it never prints a guess.
 */
export function computePositionMetrics(d: PositionLike | null | undefined): PositionMetrics | null {
  if (!d || !isPositionSide(d.side)) return null;
  if (!isPoint(d.p1) || !isPoint(d.p2) || !isPoint(d.p3)) return null;
  const entry = d.p1.price;
  if (entry <= 0) return null;
  const long = d.side === "long";
  const risk = long ? entry - d.p2.price : d.p2.price - entry;
  const reward = long ? d.p3.price - entry : entry - d.p3.price;
  if (!(risk > 0) || !(reward > 0)) return null;
  return {
    risk,
    reward,
    rr: reward / risk,
    riskPct: (risk / entry) * 100,
    rewardPct: (reward / entry) * 100,
  };
}

export interface PositionLabels {
  side: string;
  stop: string;
  target: string;
  rr: string;
}

/** Fixed-2dp price / distance text. ASCII sign so no font can drop a glyph. */
export function formatPositionLabels(d: PositionLike | null | undefined): PositionLabels | null {
  const m = computePositionMetrics(d);
  if (!m || !d || !d.p1 || !d.p2 || !d.p3) return null;
  return {
    side: d.side === "long" ? "Long" : "Short",
    stop: `Stop ${d.p2.price.toFixed(2)}  -${m.risk.toFixed(2)} (${m.riskPct.toFixed(2)}%)`,
    target: `Target ${d.p3.price.toFixed(2)}  +${m.reward.toFixed(2)} (${m.rewardPct.toFixed(2)}%)`,
    rr: `R:R ${m.rr.toFixed(2)}`,
  };
}

/**
 * Re-establishes the invariants after a drag has displaced points independently
 * (the drag engine moves each point on its own, so p2.time and p3.time drift).
 * Mutates `d` in place; a no-op for anything that is not a well-formed position.
 *
 * `mode` is the drag mode: 'move' | 'resize-p1' | 'resize-p2' | 'resize-p3'.
 * The point being dragged wins; the others are pulled into line with it.
 */
export function normalizePosition(
  d: PositionLike | null | undefined,
  mode: string,
  opts: PositionOptions = {},
): void {
  if (!d || d.type !== POSITION_TYPE || !isPositionSide(d.side)) return;
  if (!isPoint(d.p1) || !isPoint(d.p2) || !isPoint(d.p3)) return;
  const gap = opts.minPriceGap ?? MIN_PRICE_GAP;
  const tgap = opts.minTimeGap ?? 1;
  const long = d.side === "long";

  // Time: p2 and p3 share the right edge. The dragged one is authoritative.
  if (mode === "resize-p3") d.p2 = { time: d.p3.time, price: d.p2.price };
  else d.p3 = { time: d.p2.time, price: d.p3.price };

  // Time: the right edge stays after the left edge.
  if (d.p2.time - d.p1.time < tgap) {
    if (mode === "resize-p1") {
      d.p1 = { time: d.p2.time - tgap, price: d.p1.price };
    } else {
      const t = d.p1.time + tgap;
      d.p2 = { time: t, price: d.p2.price };
      d.p3 = { time: t, price: d.p3.price };
    }
  }

  // Prices. Each drag mode clamps the value it moved; 'move' clamps both as a
  // safety net (a point whose pixel->price conversion failed keeps its old value).
  const stopMax = long ? d.p1.price - gap : Infinity;
  const stopMin = long ? -Infinity : d.p1.price + gap;
  const tgtMin = long ? d.p1.price + gap : -Infinity;
  const tgtMax = long ? Infinity : d.p1.price - gap;

  if (mode === "resize-p1") {
    // Entry moved: keep it strictly inside stop..target.
    const lo = long ? d.p2.price + gap : d.p3.price + gap;
    const hi = long ? d.p3.price - gap : d.p2.price - gap;
    if (lo <= hi) {
      const price = Math.min(Math.max(d.p1.price, lo), hi);
      d.p1 = { time: d.p1.time, price };
    }
    return;
  }
  if (mode !== "resize-p3") {
    const price = Math.min(Math.max(d.p2.price, stopMin), stopMax);
    d.p2 = { time: d.p2.time, price };
  }
  if (mode !== "resize-p2") {
    const price = Math.min(Math.max(d.p3.price, tgtMin), tgtMax);
    d.p3 = { time: d.p3.time, price };
  }
}

/**
 * Sets the target to `rr` times the current risk (the popup's R:R input).
 * Returns false and changes nothing for a non-positive / non-finite rr or a
 * drawing that is not a valid position. A reward smaller than the minimum gap
 * is clamped up to the gap.
 */
export function setTargetByRR(
  d: PositionLike | null | undefined,
  rr: number,
  opts: PositionOptions = {},
): boolean {
  if (!d || d.type !== POSITION_TYPE || !isPositionSide(d.side)) return false;
  if (!isPoint(d.p1) || !isPoint(d.p2) || !isPoint(d.p3)) return false;
  const gap = opts.minPriceGap ?? MIN_PRICE_GAP;
  const target = defaultTarget(d.side, d.p1.price, d.p2.price, rr, gap);
  if (target === null) return false;
  d.p3 = { time: d.p3.time, price: target };
  normalizePosition(d, "resize-p3", opts);
  return true;
}
